import { useEffect, useState } from "react";
import { useAccount, useSwitchChain } from "wagmi";
import { useAuth } from "./index";
import { getDefaultChainId } from "app/utils/getDefaultChainId";
import { CHAIN_ID } from "app/utils/constants";

interface UseNetworkManagerProps {
  supportedNetworks: any[];
  autoSwitch?: boolean;
}

export const useNetworkManager = ({
  supportedNetworks,
  autoSwitch = true,
}: UseNetworkManagerProps) => {
  const { chainId: walletChainId } = useAccount();
  const { switchChain } = useSwitchChain();
  const isAuth = useAuth();

  const [chainId, setChainId] = useState<number>(CHAIN_ID.LINEA_MAINNET);
  const [isNetworkSupported, setIsNetworkSupported] = useState(false);

  const getOptimalChainId = (): number => {
    if (!supportedNetworks?.length) {
      console.warn(
        "NetworkManager: No supported networks provided, defaulting to LINEA_MAINNET"
      );
      return CHAIN_ID.LINEA_MAINNET;
    }

    if (
      isAuth &&
      walletChainId &&
      supportedNetworks.some((n: any) => n.chainId === walletChainId)
    ) {
      return walletChainId;
    }

    const defaultChainId = getDefaultChainId(supportedNetworks);
    console.log(
      "NetworkManager: Using default chain ID:",
      defaultChainId,
      "from supported networks:",
      supportedNetworks.map((n: any) => n.chainId)
    );
    return defaultChainId;
  };

  const checkNetworkSupport = (currentChainId: number): boolean => {
    return supportedNetworks.some(
      (network: any) => network.chainId === currentChainId
    );
  };

  useEffect(() => {
    if (!supportedNetworks?.length) return;

    const optimalChainId = getOptimalChainId();
    const currentIsSupported = checkNetworkSupport(chainId);

    if (chainId !== optimalChainId || !currentIsSupported) {
      setChainId(optimalChainId);
      setIsNetworkSupported(checkNetworkSupport(optimalChainId));
    } else {
      setIsNetworkSupported(currentIsSupported);
    }

    if (autoSwitch && isAuth && walletChainId) {
      const isWalletChainSupported = checkNetworkSupport(walletChainId);
      if (!isWalletChainSupported) {
        console.log(
          "NetworkManager: wallet chain",
          walletChainId,
          "not supported, switching to",
          optimalChainId
        );
        switchChain({ chainId: optimalChainId });
      }
    }
  }, [supportedNetworks, walletChainId, isAuth, autoSwitch]);

  const handleNetworkChange = (newChainId: number) => {
    setChainId(newChainId);
    setIsNetworkSupported(checkNetworkSupport(newChainId));

    if (isAuth) {
      switchChain({ chainId: newChainId });
    }
  };

  return {
    chainId,
    isNetworkSupported,
    handleNetworkChange,
    supportedNetworks,
  };
};
